import React from 'react';
import { useSnackbar } from 'notistack';

import { DataAPIContext, GET_ONE } from '../api';
import { DELETE_BATCH } from '../api/dataProvider';
import { BackgroundTaskContext } from './BackgroundTaskProvider';
import { isBackgroundTaskResponse, notifyBackgroundTask } from './batchResponseHelper';

export const TASK_COMPLETED = 2;
export const TASK_FAILED = 3;
export const TASK_CANCELLED = 4;
const TASK_POLL_MS = 1500;
const TASK_POLL_LIMIT = 40;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Follows one task directly rather than waiting up to 15s for the
// BackgroundTaskProvider poll. Returns the finished task, or null if it is
// still running after ~60s.
export const waitForTask = async (dataAPI, taskId, pollMs = TASK_POLL_MS) => {
    for (let i = 0; i < TASK_POLL_LIMIT; i++) {
        await sleep(pollMs);
        const res = await dataAPI(GET_ONE, 'bg_tasks', { id: taskId }).catch(() => null);
        const state = res?.statusCode === 200 ? res.data?.state : null;
        if (state === TASK_COMPLETED || state === TASK_FAILED || state === TASK_CANCELLED) {
            return res.data;
        }
    }
    return null;
};

// BatchDeleteTask's {deleted, errors, total} is read from the task's S3
// archive, which is written just after the task is marked completed.
export const fetchTaskResult = async (dataAPI, taskId, pollMs = TASK_POLL_MS) => {
    for (let i = 0; i < 3; i++) {
        const res = await dataAPI(GET_ONE, 'bg_tasks', { id: taskId, urlSuffix: 'result/' }).catch(() => null);
        if (res?.statusCode === 200 && res.data?.result) {
            return res.data.result;
        }
        await sleep(pollMs);
    }
    return null;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// A synchronous batch delete answers 204, or 200 with a JSON object. An unhandled backend error is served as an HTML
// page with status 200, which the data provider hands back as `data: []` -- not a delete (e.g. a backend without a
// batch route for the resource answers its batch/ URL that way).
const deletedAtOnce = (res) => res?.statusCode === 204
    || (res?.statusCode === 200 && !!res.data && typeof res.data === 'object' && !Array.isArray(res.data));

/**
 * Bulk delete for a grid (CP-7771): `DELETE <resource>/batch/` with the selected ids.
 *
 * - 202: the backend runs it as a background task. A toast links to Background Operations;
 *   the hook then follows the task, calls `onDeleted` once it has finished (so the grid
 *   refreshes when the rows are really gone), and says how it went. The task ends
 *   "Completed" even when some records could not be deleted (still in use), so the
 *   message comes from the task's result, not its state.
 * - 200 / 204: deleted at once (a backend that still deletes synchronously).
 * - Anything else: the backend's message (a 406 says which records are in use).
 *
 * `onDeleted({ ids, finished })`: `finished` is false when the task was still running after ~60s
 * (the rows may still exist), true otherwise.
 *
 * Usage:
 *   const { deleteRecords, deleting } = useBatchDelete({
 *       resource: 'route_stop',
 *       names: ['bus stop', 'bus stops'],
 *       onDeleted: () => { setSelectedIds([]); setLastUpdate(Date.now()); },
 *   });
 *   await deleteRecords(ids);   // true when the delete was accepted
 */
const useBatchDelete = ({ resource, names, onDeleted, pollMs }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const bgCtx = React.useContext(BackgroundTaskContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [deleting, setDeleting] = React.useState(false);
    const mountedRef = React.useRef(true);
    React.useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const onDeletedRef = React.useRef(onDeleted);
    onDeletedRef.current = onDeleted;

    const [singular, plural] = names;
    const what = React.useCallback((n) => (n === 1 ? singular : plural), [singular, plural]);

    const followTask = React.useCallback(async (taskId, ids) => {
        const count = ids.length;
        const task = await waitForTask(dataAPI, taskId, pollMs);
        onDeletedRef.current && onDeletedRef.current({ ids, finished: !!task });
        if (!task) {
            enqueueSnackbar('Still deleting. Progress is under Background Operations.', { variant: 'info' });
            return;
        }
        if (task.state !== TASK_COMPLETED) {
            enqueueSnackbar(task.error_message || `Error deleting ${what(count)}`, { variant: 'error' });
            return;
        }
        const result = await fetchTaskResult(dataAPI, taskId, pollMs);
        if (!result) {
            enqueueSnackbar('Delete finished', { variant: 'default' });
        } else if (result.total === 0) {
            enqueueSnackbar(`Nothing was deleted: you cannot delete the selected ${plural}.`, { variant: 'error' });
        } else if (result.deleted >= result.total) {
            enqueueSnackbar(result.total === 1 ? `${capitalize(singular)} deleted` : `${result.total} ${plural} deleted`, { variant: 'default' });
        } else {
            const failed = result.total - result.deleted;
            const subject = result.total === 1 ? `The ${singular}` : `${failed} of ${result.total} ${plural}`;
            const reason = result.errors?.[0]?.error;
            enqueueSnackbar(
                `${subject} could not be deleted${reason ? ` (${reason})` : ''}. Details are under Background Operations.`,
                { variant: 'error', autoHideDuration: 8000 },
            );
        }
    }, [dataAPI, enqueueSnackbar, what, singular, plural, pollMs]);

    const deleteRecords = React.useCallback(async (ids) => {
        if (!ids?.length) return false;
        setDeleting(true);
        try {
            const res = await dataAPI(DELETE_BATCH, resource, { data: { list: ids } });
            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                // Not awaited: the caller closes its dialog now; the grid refreshes when the task is done.
                followTask(res.data.task_id, ids);
                return true;
            }
            if (deletedAtOnce(res)) {
                enqueueSnackbar(ids.length === 1 ? `${capitalize(singular)} deleted` : `${ids.length} ${plural} deleted`, { variant: 'default' });
                onDeletedRef.current && onDeletedRef.current({ ids, finished: true });
                return true;
            }
            enqueueSnackbar(res?.data?.message || `Error deleting ${what(ids.length)}`, { variant: 'error', autoHideDuration: 6000 });
            return false;
        } catch (e) {
            enqueueSnackbar(`Error deleting ${what(ids.length)}`, { variant: 'error' });
            return false;
        } finally {
            if (mountedRef.current) setDeleting(false);
        }
    }, [dataAPI, resource, enqueueSnackbar, closeSnackbar, bgCtx, followTask, what, singular, plural]);

    return { deleteRecords, deleting };
};

export default useBatchDelete;
